<script setup lang="ts">
  // Day-close hero: "Ventas netas del día" big value + delta chip vs the
  // same weekday last week, a muted "Cobrado" legend (collections-based
  // money received, with its own pct), plus the KPI card row. Zero-sales
  // days render a friendly empty variant instead of a $0 wall.
  import { computed, getCurrentInstance } from 'vue';
  import V2StatsKpiCard from '@/components/v2/stats/V2StatsKpiCard.vue';
  import {
    getDayCloseCollectedDelta,
    getDayCloseCollectedDisplay,
    getDayCloseDeltaBasis,
    getDayCloseNetDisplay,
  } from '@/domain/stats/day-close-display';
  import { IDayClose } from '@/interfaces/IDayClose';
  import { displayPrice } from '@/utils';

  const props = defineProps<{
    payload: IDayClose;
    currencySymbol: string;
  }>();

  const proxy = getCurrentInstance()!.proxy;

  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  function money(value: number): string {
    return `${props.currencySymbol}${displayPrice(value, true)}`;
  }

  /** Sign-aware variant: renders −$4.210 instead of $-4.210 (accepted
   *  negative edge cases: fully-comped additive-tax net, refund-heavy
   *  Cobrado). */
  function moneySigned(value: number): string {
    return value < 0 ? `−${money(Math.abs(value))}` : money(value);
  }

  const zeroSales = computed(() => props.payload.kpis.orders === 0);

  const net = computed(() => getDayCloseNetDisplay(props.payload));
  const collected = computed(() =>
    getDayCloseCollectedDisplay(props.payload)
  );

  const heroValue = computed(() => moneySigned(net.value.netSales));

  const delta = computed<{ up: boolean; text: string } | null>(() => {
    const basis = getDayCloseDeltaBasis(props.payload, net.value.netSales);
    if (!basis) return null;
    const pct = Math.round(
      ((basis.current - basis.previous) / basis.previous) * 100
    );
    if (pct === 0) return null;
    const arrow = pct > 0 ? '▲' : '▼';
    return {
      up: pct > 0,
      text: `${arrow} ${i18nT('v2Stats.dayClose.deltaVsLastWeek', {
        pct: Math.abs(pct),
      })}`,
    };
  });

  const collectedDelta = computed(() =>
    getDayCloseCollectedDelta(props.payload, collected.value.collectedTotal)
  );

  interface IKpiCard {
    key: string;
    label: string;
    value: string;
    /** Optional info-icon tooltip (V2StatsKpiCard `info` prop). */
    info?: string;
  }

  const kpiCards = computed<IKpiCard[]>(() => {
    if (zeroSales.value) return [];
    const kpis = props.payload.kpis;
    const wf = props.payload.waterfall;
    const tips = wf.tips;
    const avgTicketCard: IKpiCard = {
      key: 'avg-ticket',
      label: i18nT('v2Stats.dayClose.avgTicket'),
      value: money(kpis.avgTicket),
    };
    if (net.value.nonMonetary > 0) {
      avgTicketCard.info = i18nT('dashboard.averageTicketNetInfo');
    }
    const cards: IKpiCard[] = [
      {
        key: 'orders',
        label: i18nT('v2Stats.dayClose.orders'),
        value: String(kpis.orders),
      },
      avgTicketCard,
    ];
    if (kpis.covers != null) {
      const card: IKpiCard = {
        key: 'covers',
        label: i18nT('v2Stats.dayClose.covers'),
        value: String(kpis.covers),
      };
      // `undefined > 0` is false: old snapshots never show the hint.
      if ((kpis.sittingsWithoutCovers ?? 0) > 0) {
        card.info = i18nT('dashboard.coversIncomplete');
      }
      cards.push(card);
    }
    // Absent on pre-feature snapshots — the card is simply not pushed.
    if (kpis.spendPerCover != null) {
      const card: IKpiCard = {
        key: 'spend-per-cover',
        label: i18nT('dashboard.spendPerCover'),
        value: money(kpis.spendPerCover),
      };
      const nonMonetary = kpis.spendPerCoverNonMonetary;
      if (nonMonetary != null && nonMonetary > 0) {
        card.info = i18nT('dashboard.spendPerCoverNonMonetaryInfo', {
          amount: money(nonMonetary),
        });
      }
      cards.push(card);
    }
    if (tips !== 0) {
      cards.push({
        key: 'tips',
        label: i18nT('v2Stats.dayClose.tips'),
        value: money(tips),
      });
    }
    return cards;
  });
</script>

<template>
  <div class="day-close-hero">
    <div v-if="zeroSales" class="hero-card is-zero">
      <p class="hero-zero-title">{{ $t('v2Stats.dayClose.heroZeroTitle') }}</p>
      <p class="hero-zero-text">{{ $t('v2Stats.dayClose.heroZeroText') }}</p>
    </div>

    <template v-else>
      <div class="hero-card">
        <p class="hero-label">{{ $t('v2Stats.dayClose.heroLabel') }}</p>
        <p class="hero-value">{{ heroValue }}</p>
        <span
          v-if="delta"
          class="hero-delta"
          :class="delta.up ? 'is-up' : 'is-down'"
        >
          {{ delta.text }}
        </span>
        <p class="hero-collected">
          {{ $t('v2Stats.dayClose.heroCollected') }}:
          <strong class="hero-collected-value">
            {{ moneySigned(collected.collectedTotal) }}
          </strong>
          <span
            v-if="collectedDelta"
            class="hero-collected-delta"
            :class="collectedDelta.up ? 'is-up' : 'is-down'"
          >
            {{ collectedDelta.up ? '▲' : '▼' }} {{ collectedDelta.pct }}%
          </span>
        </p>
      </div>

      <div class="hero-kpis">
        <V2StatsKpiCard
          v-for="card in kpiCards"
          :key="card.key"
          :label="card.label"
          :value="card.value"
          :info="card.info"
        />
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
  .day-close-hero {
    .hero-card {
      @apply tw-mb-3 tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-p-6 tw-text-center;

      .hero-label {
        @apply tw-mb-1 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-neutral-500;
      }

      .hero-value {
        @apply tw-mb-1 tw-text-4xl tw-font-bold tw-tabular-nums tw-text-neutral-900;
      }

      .hero-delta {
        @apply tw-inline-block tw-rounded-full tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-semibold;

        &.is-up {
          @apply tw-bg-emerald-100 tw-text-emerald-700;
        }

        &.is-down {
          @apply tw-bg-red-100 tw-text-red-700;
        }
      }

      .hero-collected {
        @apply tw-mt-2 tw-mb-0 tw-text-sm tw-text-neutral-500;

        .hero-collected-value {
          @apply tw-font-semibold tw-tabular-nums tw-text-neutral-700;
        }

        .hero-collected-delta {
          @apply tw-ml-1.5 tw-text-xs tw-font-semibold;

          &.is-up {
            @apply tw-text-emerald-600;
          }

          &.is-down {
            @apply tw-text-red-600;
          }
        }
      }

      .hero-zero-title {
        @apply tw-mb-1 tw-text-2xl tw-font-semibold tw-text-neutral-900;
      }

      .hero-zero-text {
        @apply tw-mb-0 tw-text-sm tw-text-neutral-500;
      }
    }

    .hero-kpis {
      @apply tw-grid tw-grid-cols-2 tw-gap-3 lg:tw-grid-cols-4;
    }
  }
</style>
